import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './RecipePage.css';

function StarDisplay({ value }) {
if (!value) return null;
return (
<div className="recipe-page__rating">
{[1,2,3,4,5].map(s => (
<span key={s} style={{color: s <= value ? '#F59E0B' : '#E5E7EB', fontSize: '1.1rem'}}></span>
))}
</div>
);
}

function formatTime(mins) {
if (!mins) return null;
if (mins < 60) return `${mins} min`;
const h = Math.floor(mins / 60);
const m = mins % 60;
return m ? `${h} hr ${m} min` : `${h} hr`;
}

export default function RecipePage() {
const { slug } = useParams();
const navigate = useNavigate();
const [recipe, setRecipe] = useState(null);
const [loading, setLoading] = useState(true);
const [error, setError] = useState('');
const [servingMultiplier, setServingMultiplier] = useState(1);
const [isFavorite, setIsFavorite] = useState(false);
const [screenAwake, setScreenAwake] = useState(false);
const [checkedSteps, setCheckedSteps] = useState({});
const [checkedIngredients, setCheckedIngredients] = useState({});
const wakeLockRef = useRef(null);

useEffect(() => {
api.get(`/recipes/${slug}`)
.then(res => {
setRecipe(res.data.recipe);
setIsFavorite(res.data.recipe.is_favorite);
})
.catch(err => {
if (err.response?.status === 404) setError('Recipe not found');
else setError('Failed to load recipe');
})
.finally(() => setLoading(false));

return () => {
if (wakeLockRef.current) {
wakeLockRef.current.release().catch(() => {});
}
};
}, [slug]);

const toggleFavorite = async () => {
try {
await api.patch(`/recipes/${slug}/favorite`);
setIsFavorite(!isFavorite);
} catch {}
};

const toggleScreenAwake = async () => {
if (screenAwake) {
if (wakeLockRef.current) {
await wakeLockRef.current.release();
wakeLockRef.current = null;
}
setScreenAwake(false);
} else {
try {
const lock = await navigator.wakeLock.request('screen');
wakeLockRef.current = lock;
setScreenAwake(true);
lock.addEventListener('release', () => setScreenAwake(false));
} catch (e) {
alert('Screen wake lock not supported on this device/browser.');
}
}
};



const scaleAmount = (amount) => {
if (!amount || servingMultiplier === 1) return amount;
const num = parseFloat(amount);
if (isNaN(num)) return amount;
const scaled = num * servingMultiplier;
if (scaled === Math.floor(scaled)) return String(scaled);
return scaled.toFixed(2).replace(/\.?0+$/, '');
};

const handlePrint = () => window.print();

if (loading) return (
<div className="page-loading">
<div className="spinner" />
<span>Loading recipe...</span>
</div>
);

if (error) return (
<div className="error-state container" style={{paddingTop: '4rem'}}>
<h2> {error}</h2>
<button className="btn btn-primary" onClick={() => navigate('/')} style={{marginTop: '1rem'}}>
Back to Home
</button>
</div>
);

const ingredients = recipe.ingredients || [];
const instructions = recipe.instructions || [];
const imageUrl = recipe.primary_image
? `https://recipe.marumba.com${recipe.primary_image}`
: null;

return (
<div className="recipe-page">
{/* Breadcrumb */}
<div className="recipe-page__breadcrumb container">
<Link to="/">Home</Link>
{recipe.category_name && (
<>
<span>›</span>
<Link to={`/search?category=${recipe.category_slug}`}>{recipe.category_name}</Link>
</>
)}
<span>›</span>
<span>{recipe.title}</span>
</div>

{/* Hero */}
<div className="recipe-page__hero">
<div className="container">
<div className="recipe-page__hero-inner">
<div className="recipe-page__hero-content">
{recipe.category_name && (
<span className="category-badge">{recipe.category_name}</span>
)}
<h1 className="recipe-page__title">{recipe.title}</h1>
{recipe.description && (
<p className="recipe-page__description">{recipe.description}</p>
)}

{/* Action Buttons — Row 1: primary actions */}
<div className="recipe-page__actions">
<button
className={`btn ${isFavorite ? 'btn-primary' : 'btn-outline'}`}
onClick={toggleFavorite}
>
{isFavorite ? ' Saved' : ' Save'}
</button>
<button
className={`btn ${screenAwake ? 'btn-primary' : 'btn-outline'}`}
onClick={toggleScreenAwake}
title="Keep screen on while cooking"
>
{screenAwake ? ' Screen On' : ' Keep Awake'}
</button>
</div>

{/* Row 2: Print, Edit, Source */}
<div className="recipe-page__action-links">
<button className="recipe-page__action-link" onClick={handlePrint}>
Print
</button>
<Link to={`/recipe/${slug}/edit`} className="recipe-page__action-link">
Edit
</Link>
{recipe.source_url && (
<a
href={recipe.source_url}
target="_blank"
rel="noopener noreferrer"
className="recipe-page__action-link"
>
Source
</a>
)}
</div>
</div>

{/* Recipe Image */}
<div className="recipe-page__hero-image">
{imageUrl ? (
<img src={imageUrl} alt={recipe.title} />
) : (
<div className="recipe-page__no-image">
<span></span>
<p style={{fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.5rem'}}>No image yet — use Edit to refresh</p>
</div>
)}
</div>
</div>
</div>
</div>

{/* Stats Bar */}
<div className="recipe-page__stats-bar">
<div className="container">
<div className="recipe-page__stats">
{recipe.prep_time_mins && (
<div className="recipe-page__stat">
<span className="recipe-page__stat-label">Prep</span>
<span className="recipe-page__stat-value">{formatTime(recipe.prep_time_mins)}</span>
</div>
)}
{recipe.cook_time_mins && (
<div className="recipe-page__stat">
<span className="recipe-page__stat-label">Cook</span>
<span className="recipe-page__stat-value">{formatTime(recipe.cook_time_mins)}</span>
</div>
)}
{recipe.total_time_mins && (
<div className="recipe-page__stat recipe-page__stat--total">
<span className="recipe-page__stat-label">Total</span>
<span className="recipe-page__stat-value">{formatTime(recipe.total_time_mins)}</span>
</div>
)}
{recipe.servings && (
<div className="recipe-page__stat">
<span className="recipe-page__stat-label">Servings</span>
<span className="recipe-page__stat-value">
{recipe.servings * servingMultiplier} {recipe.servings_unit || 'servings'}
</span>
</div>
)}
{recipe.difficulty && (
<div className="recipe-page__stat">
<span className="recipe-page__stat-label">Difficulty</span>
<span className={`recipe-card__difficulty recipe-card__difficulty--${recipe.difficulty}`}>
{recipe.difficulty}
</span>
</div>
)}
</div>
</div>
</div>

{/* Main Content */}
<div className="recipe-page__body container">
<div className="recipe-page__main">

{/* Ingredients */}
{ingredients.length > 0 && (
<section className="recipe-page__section">
<div className="recipe-page__section-header">
<h2>Ingredients</h2>
<div className="recipe-page__multiplier">
{[0.5, 1, 2].map(m => (
<button
key={m}
className={`recipe-page__multiplier-btn ${servingMultiplier === m ? 'active' : ''}`}
onClick={() => setServingMultiplier(m)}
>
{m === 0.5 ? '½x' : m === 1 ? '1x' : '2x'}
</button>
))}
</div>
</div>
<ul className="recipe-page__ingredients">
{ingredients.map((ing, i) => (
<li
key={i}
className={`recipe-page__ingredient ${checkedIngredients[i] ? 'checked' : ''}`}
onClick={() => setCheckedIngredients(prev => ({ ...prev, [i]: !prev[i] }))}
>
<span className="recipe-page__ingredient-check">
{checkedIngredients[i] ? '' : ''}
</span>
<span className="recipe-page__ingredient-amount">
{scaleAmount(ing.amount)} {ing.unit}
</span>
<span className="recipe-page__ingredient-name">
{ing.name}
{ing.notes && <span className="recipe-page__ingredient-notes">, {ing.notes}</span>}
</span>
</li>
))}
</ul>
</section>
)}

{/* Instructions */}
{instructions.length > 0 && (
<section className="recipe-page__section">
<h2>Instructions</h2>
<ol className="recipe-page__instructions">
{instructions.map((step, i) => (
<li
key={i}
className={`recipe-page__step ${checkedSteps[i] ? 'checked' : ''}`}
onClick={() => setCheckedSteps(prev => ({ ...prev, [i]: !prev[i] }))}
>
<span className="recipe-page__step-num">{step.step || i + 1}</span>
<p className="recipe-page__step-text">{step.text}</p>
</li>
))}
</ol>
</section>
)}

{/* Chef Notes */}
{recipe.chef_notes && (
<section className="recipe-page__section recipe-page__chef-notes">
<h2>‍ Chef's Notes</h2>
<p>{recipe.chef_notes}</p>
</section>
)}

{/* Nutrition */}
{recipe.nutrition && (
<section className="recipe-page__section">
<h2>Nutrition</h2>
<div className="recipe-page__nutrition">
{Object.entries(recipe.nutrition).map(([key, val]) => val && (
<div key={key} className="recipe-page__nutrition-item">
<span className="recipe-page__nutrition-value">{val}</span>
<span className="recipe-page__nutrition-label">
{key.charAt(0).toUpperCase() + key.slice(1)}
</span>
</div>
))}
</div>
</section>
)}
</div>

{/* Sidebar */}
<aside className="recipe-page__sidebar">
{recipe.rating && (
<div className="recipe-page__tags">
<h4>My Rating</h4>
<StarDisplay value={recipe.rating} />
</div>
)}
{recipe.tags && recipe.tags.length > 0 && (
<div className="recipe-page__tags">
<h4>Tags</h4>
<div className="recipe-page__tag-list">
{recipe.tags.map((tag, i) => (
<Link
key={i}
to={`/search?q=${encodeURIComponent(tag)}`}
className="recipe-page__tag"
>
{tag}
</Link>
))}
</div>
</div>
)}

{recipe.source_url && (
<div className="recipe-page__source">
<h4>Original Recipe</h4>
<a href={recipe.source_url} target="_blank" rel="noopener noreferrer">
View on source site →
</a>
</div>
)}

{recipe.personal_notes && (
<div className="recipe-page__personal-notes">
<h4>My Notes</h4>
<p>{recipe.personal_notes}</p>
</div>
)}
</aside>
</div>
</div>
);
}
